import { defineStore } from "pinia";
import { computed } from "vue";
import { pickBy } from "lodash-es";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { useChartStore } from "../charts/charts.store";
import type { SceneNodeItem } from "../scene/SceneNodeItem";
import type { Subset } from "@/helpers/utility/Type";
import type { SceneChartData } from "./sceneChartData";
import type { ChartItem } from "../charts/ChartItem";

const chartDefaults: Subset<ChartItem> = {
  chartType: "bar",
  chartSettings: {
    scaleColorsVisible: false,
    roundEffectsVisible: false
  }
};

export const useSceneChartStore = defineStore("model/sceneCharts", () => {
  const sceneNodeStore = useSceneNodesStore();
  const chartStore = useChartStore();

  function isChartItem(item: SceneNodeItem): item is SceneNodeItem<"chart"> {
    return item.dataType === "chart";
  }

  async function create(data: SceneChartData | Subset<ChartItem>, options: Subset<SceneNodeItem<"chart">>) {
    if (!("chartUid" in data)) {
      const chart = await chartStore.create({ ...chartDefaults, ...data });
      data = {
        chartUid: chart.uid
      };
    }

    const optionsWithData = {
      ...options,
      dataType: "chart" as const,
      data: data,
    };
    return await sceneNodeStore.create(optionsWithData);
  }

  const items = computed(() => {
    return pickBy(sceneNodeStore.items, isChartItem) as Record<number, SceneNodeItem<"chart">>;
  });

  const byUid = computed(() => {
    return pickBy(sceneNodeStore.byUid, isChartItem) as Record<string, SceneNodeItem<"chart">>;
  });

  function $reset() {
    // handled by sceneNodeStore.
    return;
  }

  return {
    create,
    items,
    byUid,
    $reset
  }
});
